/**
 * The badger launcher's call-out: the short line in the speech bubble beside the
 * corner badger, and the timing and storage rules around it. Pure data and a pure
 * function, so the table is testable without rendering.
 */

/** How long after the first page load of a session the call-out first appears. */
export const CALLOUT_SHOW_DELAY_MS = 3000;
/** How long the automatic call-out stays up before hiding itself. */
export const CALLOUT_VISIBLE_MS = 6000;
/** sessionStorage key (value '1') marking that this session already had its call-out. */
export const CALLOUT_SEEN_KEY = 'badger-callout-seen';

export const CALLOUT_FALLBACK = 'Need help? Click me.';
/** Shown, every time, when a reply finishes while the window is closed. */
export const CALLOUT_UNREAD_TEXT = 'I have an answer for you';

// Most specific prefix first; the first match wins. A prefix matches on a path
// segment boundary (the path equals it or continues with "/"), so "/clientsx" does
// not match "/clients". Prefixes are the app's real routes (App.tsx).
const ADS_LINE = 'Questions about this account?';
const ARTICLE_LINE = 'Need a hand with this article?';
const ROUTE_LINES: ReadonlyArray<{ prefix: string; line: string }> = [
  { prefix: '/clients/ads', line: ADS_LINE },
  { prefix: '/google-ads', line: ADS_LINE },
  { prefix: '/ads-tools', line: ADS_LINE },
  { prefix: '/clients', line: 'Adding a client? I can walk you through it.' },
  { prefix: '/audit/tickets', line: 'Need help with a ticket?' },
  { prefix: '/audit', line: 'Want me to explain these issues?' },
  { prefix: '/brand-bible', line: 'Want help filling in the brand bible?' },
  { prefix: '/seo', line: 'Want me to walk you through these numbers?' },
  { prefix: '/content', line: ARTICLE_LINE },
  { prefix: '/blog', line: ARTICLE_LINE },
];

function matchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** The page-aware line for a route. Query string and hash are ignored. */
export function calloutTextFor(pathname: string): string {
  const path = (pathname || '').split(/[?#]/)[0];
  const hit = ROUTE_LINES.find(({ prefix }) => matchesPrefix(path, prefix));
  return hit ? hit.line : CALLOUT_FALLBACK;
}

/** Every storage access can throw (blocked storage, private mode). Throwing means
 *  "already shown": the call-out must never nag a visitor we cannot remember. */
export function calloutAlreadySeen(): boolean {
  try {
    return window.sessionStorage.getItem(CALLOUT_SEEN_KEY) === '1';
  } catch {
    return true;
  }
}

export function markCalloutSeen(): void {
  try {
    window.sessionStorage.setItem(CALLOUT_SEEN_KEY, '1');
  } catch {
    /* never nag; nothing to remember */
  }
}
